import { useMessages } from "../language/language"
import NavLogo from "../components/nav-logo"
import TopNav from "../components/top-nav"
import "./layout.less"
import ScreenPoints from "../components/screen-points"
import RoundedContainer from "../components/nav-rounded-container"
import ResponsiveContainer from "../components/responsive-container"
import ResponsiveContent from "../components/responsive-content"
import ScreenPager from "../components/screen-pager"
import { awaitMilliseconds, boolOr, clamp, classNames } from "utils/helpers"
import AnswerExplanation from "../components/answer-explanation"
import ShadowOverContainer from "../components/shadow-over-container"
import DefaultTopNavLinks from "../frames/top-nav-default-links"
import Timer from "../components/timer"
import TimerProgress from "../components/timer-progress"
import {
  serverPlayNextScreen,
  serverPlayReviewPrevious,
  serverPlayTimerTimedOut,
  serverPlayToggleTimer,
} from "utils/server"
import { PropsWithChildren, useEffect, useRef } from "react"
import {
  applyElapsedMsToState,
  applyElapsedSecondsToState,
  clearPlayErrorReducer,
  disableTimer,
  finishScreenReducer,
  playErrorReducer,
  requestNextScreenReducer,
  reviewRequestFailedReducer,
  reviewRequestSucceededReducer,
  screenFinishedReducer,
  serverRequestErrorReducer,
} from "play/reducers"
import LessonBackground from "../components/lesson-background"
import { usePlayState } from "play/hooks/use-play-state"
import {
  PlayHubState,
  ScreenFinishedResponse,
  SessionMode,
} from "play/play-model"
import {
  globalModifyPlayState,
  oncePerUpdate,
} from "play/context/play-context"

type Props = {
  timerEnabled?: boolean
  screenCounterEnabled?: boolean
  nextEnabled?: boolean
  prevEnabled?: boolean
  pointsEnabled?: boolean
  scrollingEnabled?: boolean
  logoIsLink?: boolean
  hasFooter?: boolean
}

function noOp() {}

export function Layout(props: PropsWithChildren<Props>) {
  const {
    connectionId,
    points,
    timer,
    screenIndex,
    countScreens,
    navigationIsLoading,
    nextScreenButtonIsVisible,
    prevScreenButtonIsVisible,
    showAnswerExplanation,
    screen,
    answerExplanationPosition,
    isTeacherUrl,
    isPreview,
    countAnsweredStudents,
    countStartedStudents,
    nextScreenButtonIsActive,
    prevScreenButtonIsActive,
    isBusinessVersion,
    theme,
    modifyState,
    mode,
    gameCode,
  } = usePlayState()
  const answerExplanation = screen?.answerExplanation || ""

  const timerVisible = boolOr(props.timerEnabled, timer.visible)
  const nextVisible = boolOr(props.nextEnabled, nextScreenButtonIsVisible)
  const prevVisible = boolOr(props.prevEnabled, prevScreenButtonIsVisible)
  const screenCounterEnabled = boolOr(props.screenCounterEnabled, true)
  const pointsEnabled = boolOr(props.pointsEnabled, !isPreview)
  const scrollingEnabled = boolOr(props.scrollingEnabled, false)
  const logoIsLink = boolOr(props.logoIsLink, false)
  const hasFooter = boolOr(props.hasFooter, true)
  const isLoading = useRef(false)

  const isLoadingUI = navigationIsLoading || isLoading.current

  function studentAnswerCompleted(
    state: PlayHubState,
    resp?: ScreenFinishedResponse
  ) {
    // note: this is set in screenFinishedAction, therefore it should be in
    // loading state until screen change actually happens
    // state.navigationIsLoading = false

    if (state.mode === SessionMode.Classroom) {
      if (state.isTeacherUrl) {
        // do nothing, wait for broadcast event
        // since we initiated the screen change, it won't take long, and the UI is disabled anyway
      } else {
        // wait for broadcast event, but screen change will happen
        // only when all have answered (broadcast) or the teacher changes the screen =>
        // we have to wait longer
        state.loadingReason = "OtherStudentsAnswering"
        state.internalPage = "Loading"
        state.nextTimer = resp || null
      }
    } else {
      // load immediately
      screenFinishedReducer(state, resp)
    }
  }

  async function timerCollectAnswers() {
    function onError(e: any) {
      console.error(e)
      modifyState((state) => {
        state.internalPage = null
        state.loadingReason = null
        playErrorReducer(state, "TimerTimeout", e)
      })
    }

    function onDoneWithState(state: PlayHubState) {
      state.internalPage = null
      state.loadingReason = null
      state.timer.running = false
    }

    if (!connectionId) return onError("connection id is empty")

    const initialScreenId = screen?.id!

    // stop timer to collect answers
    modifyState((state) => {
      state.internalPage = "Loading"
      state.loadingReason = isTeacherUrl
        ? "TeacherWaitsForStudentFeedback"
        : "OtherStudentsAnswering"
      state.timer.running = false
      state.timer.visible = false
    })

    try {
      // tell students to send answers
      await serverPlayTimerTimedOut(connectionId)

      // give students some time
      await awaitMilliseconds(2_000)

      modifyState((state) => {
        let newScreenIndex = state.screen?.id
        if (newScreenIndex === initialScreenId) {
          console.log("forcefully switch to next screen, if not already there")
          oncePerUpdate(() =>
            serverPlayNextScreen(connectionId, {
              isTimeout: true,
              screenId: initialScreenId,
            })
              .then((nextScreen) => {
                modifyState((state) => {
                  studentAnswerCompleted(state, nextScreen)
                  onDoneWithState(state)
                })
              })
              .catch(onError)
          )
        } else {
          // screen already changed
          console.log(
            "already at new screen index, {0}->{1}",
            initialScreenId,
            newScreenIndex
          )
          onDoneWithState(state)
        }
      })
    } catch (e) {
      onError(e)
    }
  }

  useEffect(() => {
    const { visible, running, timerOffsetMs, totalSeconds, initialTimestamp } =
      timer
    const totalMilliseconds = totalSeconds * 1_000
    let isTicking = true

    function onDone() {
      isTicking = false
      modifyState((state) => {
        // disable any interaction

        state.prevScreenButtonIsVisible = false
        state.nextScreenButtonIsVisible = false
        state.finished = true

        // now, we wait for signal from server
        if (state.mode === SessionMode.Classroom) {
          if (state.isTeacherUrl) {
            state.loadingReason = "TeacherWaitsForStudentFeedback"
          } else {
            state.loadingReason = "OtherStudentsAnswering"
          }
          state.internalPage = "Loading"
        } else {
        }
      })
    }

    function timerStep(timestamp: DOMHighResTimeStamp) {
      if (!running || !visible || !isTicking) {
        return
      }

      let start = initialTimestamp
      let isFirst = false
      if (!start) {
        start = timestamp
        modifyState((state) => {
          state.timer.initialTimestamp = timestamp
        })
        isFirst = true
      }

      let elapsedMs = clamp(
        timestamp - start + timerOffsetMs,
        0,
        totalMilliseconds
      )

      modifyState((state) => {
        applyElapsedMsToState(state, { elapsedMs })
      })

      if (elapsedMs < totalMilliseconds) {
        if (running) {
          requestAnimationFrame(timerStep)
        }
      } else {
        if (totalSeconds > 0) {
          // note: only teacher can call this
          if (mode === SessionMode.Classroom) {
            // when in classroom - teacher notifies all students
            if (isTeacherUrl) {
              timerCollectAnswers()
            }
          } else {
            // when in homework, directly give answers (i.e. notify self)
            //dispatch(timerSlice.actions.timerTimedOutOnServer())
            modifyState((state) => {
              if (!state.isTeacherUrl) {
                finishScreenReducer(state, { timeout: true })
              }
            })
          }

          // DONE - DO NOT REQUEST ANIMATION FRAME
          onDone()
        }
      }
    }

    if (timer.running && timer.visible) {
      requestAnimationFrame(timerStep)
    }

    if (!timer.running) {
      isTicking = false
    }

    return () => {
      isTicking = false
    }
  }, [
    timer.visible,
    timer.running,
    timer.timerOffsetMs,
    timer.totalSeconds,
    timer.initialTimestamp,
    mode,
    isTeacherUrl,
  ])

  function handleNextRequested() {
    modifyState((state) => {
      if (isLoading.current) return
      isLoading.current = true

      requestNextScreenReducer(state)
        .then(() => {
          modifyState((state) => {
            isLoading.current = false
          })
        })
        .catch((e) => {
          console.warn(e)
          isLoading.current = false
        })
    })
  }

  function handlePrevRequested() {
    if (isLoading.current) return
    isLoading.current = true

    modifyState((state) => {
      prevRequested(state)
        .then(() => (isLoading.current = false))
        .catch((e) => {
          console.warn(e)
          isLoading.current = false
        })
    })
  }

  function prevRequested(state: PlayHubState): Promise<void> {
    if (!state.prevScreenButtonIsVisible) {
      return Promise.reject("prev button must be visible")
    }
    if (state.navigationIsLoading) {
      return Promise.reject("prev button must not be loading")
    }
    if (!state.prevScreenButtonIsActive) {
      return Promise.reject("prev button must be active")
    }

    if (!state.canReviewPrevScreen) {
      return Promise.reject("can't review prev screen")
    }
    if (!state.connectionId) {
      return Promise.reject("no connection id")
    }

    state.navigationIsLoading = true
    clearPlayErrorReducer(state)
    disableTimer(state)
    const connectionId = state.connectionId
    return oncePerUpdate(() =>
      serverPlayReviewPrevious(connectionId)
        .then((resp) => {
          globalModifyPlayState((state) => {
            reviewRequestSucceededReducer(state, resp)
          })
        })
        .catch((e) => {
          globalModifyPlayState((state) => {
            reviewRequestFailedReducer(state, e, "ReviewPrevious")
          })
        })
    )
  }

  function handleToggleTimer() {
    // toggle before request
    if (isLoading.current) return
    isLoading.current = true

    const newState = !timer.running

    modifyState((state) => {
      state.timer.running = !state.timer.running
      clearPlayErrorReducer(state)
    })

    if (!connectionId) {
      return console.log("connectionId is empty")
    }

    serverPlayToggleTimer({
      connectionId: connectionId,
      enable: newState,
      secondsRemaining: timer.secondsRemaining,
    })
      .then((resp) => {
        modifyState((state) => {
          isLoading.current = false

          state.timer.secondsRemaining = resp.secondsRemaining
          state.timer.running = resp.isEnabled
          state.timer.visible = true
          state.timer.initialTimestamp = null

          //const elapsedMs = state.timer.timerOffsetMs

          applyElapsedSecondsToState(state, {
            timerSecondsRemaining: resp.secondsRemaining,
          })
        })
      })
      .catch((e) => {
        isLoading.current = false
        modifyState((state) => {
          // the button was toggled before the request: put it back
          state.timer.running = !newState
          serverRequestErrorReducer(state, e)
          playErrorReducer(state, "ToggleTimer", e)
        })
      })
  }

  const children = props.children

  const msg = useMessages()

  let nextCallback = noOp

  if (!isPreview && nextScreenButtonIsActive) {
    nextCallback = handleNextRequested
  }

  // console.log({isTeacher})

  return (
    <div
      className={classNames("layout", {
        scrollable: scrollingEnabled,
        "no-footer": !hasFooter,
      })}
    >
      <TopNav isMenuInvisible={isPreview}>
        <div className="menu">
          <DefaultTopNavLinks />
          {isTeacherUrl ? (
            <RoundedContainer visible={pointsEnabled}>
              {countAnsweredStudents} / {countStartedStudents}
            </RoundedContainer>
          ) : (
            <ScreenPoints
              points={points}
              pointsFormatter={msg.pointsToString}
              visible={pointsEnabled}
              invisibleClass="hidden"
            />
          )}
          <NavLogo className="menu-logo" isLink={logoIsLink} />
          <Timer visible={timerVisible} />
        </div>
      </TopNav>

      <ResponsiveContainer
        className="screen-page-content"
        centerContents={false}
      >
        <>
          <LessonBackground
            isBusinessVersion={isBusinessVersion}
            theme={theme}
          />
          <ResponsiveContent className="screen-page-content-inner">
            <TimerProgress isVisible={timerVisible} />
            {children}
            {showAnswerExplanation && (
              <AnswerExplanation
                answerExplanation={answerExplanation}
                position={answerExplanationPosition}
              />
            )}
          </ResponsiveContent>
          {showAnswerExplanation && <ShadowOverContainer />}
        </>
      </ResponsiveContainer>

      <ResponsiveContainer
        className="footer"
        centerContents={true}
        visible={hasFooter}
      >
        <ResponsiveContent className="footer-inner">
          <button
            className={classNames("prev", {
              invisible: isPreview || isLoadingUI || !prevVisible,
              active: !isPreview && prevScreenButtonIsActive,
            })}
            onClick={handlePrevRequested}
            draggable={false}
            disabled={isLoadingUI}
          >
            <i />
          </button>
          {isTeacherUrl && (
            <RoundedContainer className="game-code">
              {gameCode}
            </RoundedContainer>
          )}
          <ScreenPager
            className={classNames("pager", {
              invisible: !screenCounterEnabled,
            })}
            current={screenIndex}
            total={countScreens}
            size="small"
          />
          <ToggleTimerButton
            className={classNames("timer-control active", {
              on: !timer.running,
              off: timer.running,
              invisible:
                !timerVisible || isLoadingUI || isPreview || !isTeacherUrl,
            })}
            onClick={handleToggleTimer}
            disabled={isLoadingUI || !connectionId}
          />
          <button
            className={classNames("next", {
              invisible: isPreview || isLoadingUI || !nextVisible,
              "active animated-shadow": !isPreview && nextScreenButtonIsActive,
            })}
            onClick={nextCallback}
            draggable={false}
            disabled={isLoadingUI}
          >
            <i />
          </button>
        </ResponsiveContent>
      </ResponsiveContainer>
    </div>
  )
}

function ToggleTimerButton(props: {
  className: string
  onClick(): void
  disabled: boolean
}) {
  const { className, onClick, disabled } = props

  return (
    <button
      className={className}
      disabled={disabled}
      onClick={onClick}
      draggable={false}
    >
      <i />
    </button>
  )
}

export default Layout
